import React, { useEffect, useState, useMemo } from 'react';
import SearchIcon from '@material-ui/icons/Search';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import moment from 'moment';
import {
    TextField,
    Card,
    Breadcrumbs,
    Link,
    Typography,
    CardContent,
    Container,
    Grid,
    Backdrop,
    CircularProgress,
    Button,
    Snackbar,
    ThemeProvider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Divider,
    CardHeader
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import Page from 'src/components/Page';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import DateFnsUtils from '@date-io/date-fns';
import { createMuiTheme } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
    KeyboardDatePicker,
    MuiPickersUtilsProvider
} from '@material-ui/pickers';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { paymentActions, paymentThunks } from 'src/store/slices/paymentSlice';
import { getUser, moneyFormat } from 'src/utils/Common';
import { exportToExcel } from 'src/utils/Common';
import { Close, Payment, Save,Edit } from '@material-ui/icons';
import SendIcon from '@material-ui/icons/Send';
import ProductOrderDetail from 'src/views/SironexPayments/ProductOrderDetail';
import Moment from 'moment';



const SessionUser = getUser();

const localeMap = {
    tr: require('date-fns/locale/tr')
};
const locale = localStorage.getItem('i18nextLng');
const siyah = '#424242';

const materialTheme = createMuiTheme({
    palette: {
        primary: {
            contrastText: '#FFFFFF',
            dark: siyah,
            main: siyah,
            light: siyah
        }
    }
});

const useStyles = makeStyles(theme => ({
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0)'
    },
    root: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
    },
    cardscontent: {
        minHeight: 300
    },
    backdrop: {
        zIndex: 1500,
        color: '#fff'
    },
    tableRow: {
        '&$hover:hover': {
            backgroundColor: '#E4DCCF'
        }
    },
    tableCell: {
        '$hover:hover &': {
            color: 'black'
        }
    },
    hover: {},
}));

const SironexPayments = () => {
    const classes = useStyles();
    const { t } = useTranslation('overview');
    const dispatch = useDispatch();

    // Product Order Status List (separate from warranty statuses)
    const productOrderStatusList = [
        { Id: 1, Name: 'New' },
        { Id: 2, Name: 'Waiting for Payment' },
        { Id: 3, Name: 'Paid' },
        { Id: 4, Name: 'SAP Sent' },
        { Id: 5, Name: 'Rejected' }
    ];

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
    const [lastResendMap, setLastResendMap] = useState({}); // { [workOrderId]: timestamp }
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [approvalType, setApprovalType] = useState('approved');
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [description, setDescription] = useState('');
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [selectedOrderStatusId, setSelectedJobStatus] = useState(null);

    // Redux state
    const employeeList = useSelector(state => state.payment.employeeList || []);
    const selectedEmployee = useSelector(state => state.payment.selectedEmployee);
    const statusList = useSelector(state => state.payment.statusList || []);
    const selectedStatus = useSelector(state => state.payment.selectedStatus);
    const selectedCreateDate = useSelector(state => state.payment.selectedCreateDate);
    const paymentDataList = useSelector(state => state.payment.paymentDataList || []);
    // product orders from slice
    const productOrderList = useSelector(state => state.payment.productOrderList || []);
    const repairOrderList = useSelector(state => state.payment.repairOrderList || []);
    const loading = useSelector(state => state.payment.loading) === 'Loading';
    const error = useSelector(state => state.payment.error);
    const resendResult = useSelector(state => state.payment.resendResult); // adapt if needed

    // Local state for tab + search
    const [activeTab, setActiveTab] = useState('warranty'); // 'warranty' | 'product'
    const [searchTerm, setSearchTerm] = useState('');
    // Product orders pagination
    const [productPage, setProductPage] = useState(0);
    const productPageSize = 20;
    const [repairPage, setRepairPage] = useState(0);
    const repairPageSize = 20;

    // Product Orders filters
    const [productSelectedStatusId, setProductSelectedStatusId] = useState(null);
    const [productSelectedStartDate, setProductSelectedStartDate] = useState(null);
    const [productSelectedEndDate, setProductSelectedEndDate] = useState(null);
    const [productSelectedProvider, setProductSelectedProvider] = useState(null);

    // Snackbar close
    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setOpenSnackbar(false);
    };

    // Fetch filter data (warranty tab)
    useEffect(() => {
        dispatch(paymentThunks.GetEmployees());
        dispatch(paymentThunks.GetStatuses());
    }, [dispatch]);

    // When switching to product/repair tab, fetch orders once
    useEffect(() => {
        if (activeTab === 'product') {
            dispatch(paymentThunks.GetProductOrderList());
        } else if (activeTab === 'repair') {
            dispatch(paymentThunks.GetRepairOrderList());
        }
    }, [activeTab, dispatch]);

    // Reset page when switching tab or search changes
    useEffect(() => {
        if (activeTab === 'product') setProductPage(0);
        if (activeTab === 'repair') setRepairPage(0);
    }, [activeTab]);
    useEffect(() => {
        setProductPage(0);
        setRepairPage(0);
    }, [searchTerm]);

    // Search handler
    const handleSearch = () => {
        const dto = {
            EmployeeId: selectedEmployee?.Id || 0,
            StatusId: selectedStatus?.Id || 0,
            CreateDate: selectedCreateDate || null
        };
        dispatch(paymentThunks.SearchPayments(dto));
    };

    const emptyTable = () => {
        dispatch(paymentActions.clearPaymentDataList())
    }

    // Resend button handler
    const handleResend = async (workOrderId) => {
        const now = Date.now();
        const lastSent = lastResendMap[workOrderId];

        if (lastSent && now - lastSent < 5 * 60 * 1000) {
            setSnackbarMessage('Bu iş emri için 5 dakika içinde tekrar gönderim yapılamaz. Lütfen biraz bekleyin.');
            setOpenSnackbar(true);
            return;
        }

        const dto = {
            SessionIdOrReference: workOrderId.toString()
        };

        await dispatch(paymentThunks.ResendProcessPayment(dto))
            .then(response => {
                if (response.payload == true) {
                    setSnackbarMessage('Resend action completed');
                    setOpenSnackbar(true);
                    // Update the last resend time
                    setLastResendMap(prev => ({
                        ...prev,
                        [workOrderId]: now
                    }));

                }
                else {
                    setSnackbarMessage('Resend action failed: ' + response.payload.Message);
                    setOpenSnackbar(true);
                }
            })
            .catch(error => {
                setSnackbarMessage('Resend action failed: ' + error.message);
                setOpenSnackbar(true);
            });


    };
    // Warranty search filter (existing)
    const filteredPaymentsData = useMemo(() => {
        const rawTerm = searchTerm.trim();
        const term = rawTerm.toLowerCase();
        if (!term) return paymentDataList;
        return paymentDataList.filter(row => {
            const priceMatch = (() => {
                if (row.Price === undefined || row.Price === null) return false;
                const priceStr = String(row.Price);
                if (priceStr.includes(rawTerm)) return true;
                const digitsPrice = priceStr.replace(/\D/g, '');
                const digitsTerm = rawTerm.replace(/\D/g, '');
                return !!digitsTerm && digitsPrice.includes(digitsTerm);
            })();
            const genericMatch = [
                row.WorkCaseId,
                row.WorkOrderId,
                row.CustomerName,
                row.EmployeeName,
                row.ProductName,
                row.CustomerPhone,
                row.PaymentStatus
            ].some(val =>
                val !== undefined &&
                val !== null &&
                String(val).toLowerCase().includes(term)
            );
            return genericMatch || priceMatch;
        });
    }, [paymentDataList, searchTerm]);

    // Extract unique providers from product order list
    const providerList = useMemo(() => {
        const providers = new Set();
        (productOrderList || []).forEach(po => {
            if (po.Provider) {
                providers.add(po.Provider);
            }
        });
        return Array.from(providers).map(provider => ({
            Name: provider,
            Id: provider
        })).sort((a, b) => a.Name.localeCompare(b.Name));
    }, [productOrderList]);

    // ProductOrder string search with filters
    const filteredProductOrders = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        let filtered = (productOrderList || []).filter(po => {
            // String search
            if (term && ! [
                po.Id,
                po.TenantId,
                po.ProductOrderStatusId,
                po.WorkOrderId,
                po.CreateDate,
                po.CreateEmployeeId,
                po.ErpDocumentNr,
                po.ConsumerId,
                po.ProductOrderStatusName,
                po.CreateEmployeeName,
                po.SelectedEmployeeName,
                po.ConsumerName,
                po.ConsumerPhone,
                po.ConsumerAddress,
                po.ProductSku,
                po.Provider,
                po.Method,
                po.TotalAmountPaid
            ].some(v =>
                v !== undefined &&
                v !== null &&
                String(v).toLowerCase().includes(term)
            )) {
                return false;
            }

            // Status filter
            if (productSelectedStatusId && po.ProductOrderStatusId !== productSelectedStatusId) {
                return false;
            }

            // Provider filter
            if (productSelectedProvider && po.Provider !== productSelectedProvider) {
                return false;
            }

            // Date range filter
            if (productSelectedStartDate || productSelectedEndDate) {
                const poDate = po.CreateDate ? new Date(po.CreateDate) : null;
                if (productSelectedStartDate) {
                    const startDate = new Date(productSelectedStartDate);
                    startDate.setHours(0, 0, 0, 0);
                    if (!poDate || poDate < startDate) return false;
                }
                if (productSelectedEndDate) {
                    const endDate = new Date(productSelectedEndDate);
                    endDate.setHours(23, 59, 59, 999);
                    if (!poDate || poDate > endDate) return false;
                }
            }

            return true;
        });
        return filtered;
    }, [productOrderList, searchTerm, productSelectedStatusId, productSelectedProvider, productSelectedStartDate, productSelectedEndDate]);

    // Page slice for product orders
    const productTotalPages = Math.max(1, Math.ceil((filteredProductOrders?.length || 0) / productPageSize));
    const productPageStart = productPage * productPageSize;
    const productPageItems = (filteredProductOrders || []).slice(productPageStart, productPageStart + productPageSize);

    // Export filtered product orders to Excel
    const exportProductOrders = () => {
        try {
            const ExportData = (filteredProductOrders || []).map(po => ({
                Id: po.Id,
                OrderStatus: po.ProductOrderStatusName,
                WorkOrderId: po.WorkOrderId,
                CreateDate: po.CreateDate,
                CreateEmployee: po.CreateEmployeeName,
                SelectedEmployee: po.SelectedEmployeeName,
                ErpDocumentNr: po.ErpDocumentNr,
                ConsumerName: po.ConsumerName,
                ConsumerPhone: po.ConsumerPhone,
                ConsumerAddress: po.ConsumerAddress,
                ProductSku: po.ProductSku,
                Provider: po.Provider,
                Method: po.Method,
                TotalPaidAmount: moneyFormat(po.TotalAmountPaid, SessionUser.TenantCode, SessionUser.CurrencyUnit) + ' ' + SessionUser.CurrencyUnit
            }));

            const fileName = `SironexProductOrdersExport_${moment().format('YYYY-MM-DD')}`;
            exportToExcel(ExportData, fileName);
        } catch (error) {
            setSnackbarMessage('Export failed: ' + (error?.message || error));
            setOpenSnackbar(true);
        }
    };

    // Repair Orders filtering and pagination
    const filteredRepairOrders = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return repairOrderList;
        return repairOrderList.filter(ro =>
            [
                ro.Id,
                ro.WorkOrderId,
                ro.CreateEmployeeName,
                ro.ConsumerName,
                ro.Provider,
                ro.Method,
                ro.PaymentReference,
                ro.TotalAmount
            ].some(v =>
                v !== undefined &&
                v !== null &&
                String(v).toLowerCase().includes(term)
            )
        );
    }, [repairOrderList, searchTerm]);

    const repairTotalPages = Math.max(1, Math.ceil((filteredRepairOrders?.length || 0) / repairPageSize));
    const repairPageStart = repairPage * repairPageSize;
    const repairPageItems = (filteredRepairOrders || []).slice(repairPageStart, repairPageStart + repairPageSize);

    const exportRepairOrders = () => {
        try {
            const ExportData = (filteredRepairOrders || []).map(ro => ({
                Id: ro.Id,
                WorkOrderId: ro.WorkOrderId,
                CreateDate: ro.CreateDate,
                CreateEmployee: ro.CreateEmployeeName,
                ConsumerName: ro.ConsumerName,
                Provider: ro.Provider,
                Method: ro.Method,
                PaymentReference: ro.PaymentReference,
                TotalAmount: ro.TotalAmount
            }));
            const fileName = `SironexRepairOrdersExport_${moment().format('YYYY-MM-DD')}`;
            exportToExcel(ExportData, fileName);
        } catch (error) {
            setSnackbarMessage('Export failed: ' + (error?.message || error));
            setOpenSnackbar(true);
        }
    };

    const handleMouseEnter = (index) => setHoveredRowIndex(index);
    const handleMouseLeave = () => setHoveredRowIndex(null);

    const handleApproveDialogClick = (orderId, Provider, orderStatusId) => {
        setSelectedOrderId(orderId);
        setApprovalType(null); // Reset to null
        setDescription(''); // Reset description
        setApproveDialogOpen(true);
    };

    const handleApproveConfirm = () => {
        const req ={
            ProductOrderId: selectedOrderId,
            ApprovalType: approvalType,
            Description: description
        }
        dispatch(paymentThunks.ApproveBankTransfer(req));
        dispatch(paymentThunks.GetProductOrderList()); // Refresh list after approval
        setApproveDialogOpen(false);
        setSelectedOrderId(null);
    };

    const handleApproveCancel = () => {
    setApproveDialogOpen(false);
    setSelectedOrderId(null);
    setApprovalType(null); // Reset
    setDescription(''); // Reset
    };

    // Send to SAP button handler (Paid orders without an ERP document number)
    const handleSendToSap = (orderId) => {
        dispatch(paymentThunks.SendProductSalesToSap({ OrderId: orderId }))
            .then(response => {
                const result = response.payload;
                if (result?.IsSuccess) {
                    setSnackbarMessage(`Order sent to SAP successfully. SAP Nr: ${result.SapNr}`);
                    dispatch(paymentThunks.GetProductOrderList()); // Refresh list to reflect ErpDocumentNr
                } else {
                    setSnackbarMessage('Send to SAP failed: ' + (result?.ErrorMessage || result?.Message || 'Unknown error'));
                }
                setOpenSnackbar(true);
            })
            .catch(error => {
                setSnackbarMessage('Send to SAP failed: ' + error.message);
                setOpenSnackbar(true);
            });
    };
    //Product Order Detail Dialog
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedDetailOrderId, setSelectedDetailOrderId] = useState(null);
    const [selectedDetailProvider, setSelectedDetailProvider] = useState(null);
    const [selectedDetailConsumerId, setSelectedDetailConsumerId] = useState(null);

    const handleDetailDialogOpen = (orderId, consumerId) => {
        setSelectedDetailOrderId(orderId);
        setSelectedDetailConsumerId(consumerId);
        setDetailDialogOpen(true);
    };

    const handleDetailDialogClose = () => {
        setDetailDialogOpen(false);
        setSelectedDetailOrderId(null);
        setSelectedDetailProvider(null);
    };

    return (
        <Page>
            <Backdrop className={classes.backdrop} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Snackbar
                open={openSnackbar}
                autoHideDuration={10000}
                onClose={handleSnackbarClose}
                message={snackbarMessage}
            />
            <Container maxWidth="xl">
                <Breadcrumbs>
                    <Link color="inherit">Sirius</Link>
                    <Typography color="textPrimary">Sironex Payments</Typography>
                </Breadcrumbs>
                <br />
                {/* Tab toggle buttons */}
                <Grid container spacing={2} style={{ marginBottom: 8 }}>
                    <Grid item>
                        <Button
                            variant={activeTab === 'warranty' ? 'contained' : 'outlined'}
                            color="primary"
                            onClick={() => setActiveTab('warranty')}
                        >
                            Warranty Orders
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant={activeTab === 'product' ? 'contained' : 'outlined'}
                            color="primary"
                            onClick={() => setActiveTab('product')}
                        >
                            Product Orders
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant={activeTab === 'repair' ? 'contained' : 'outlined'}
                            color="primary"
                            onClick={() => setActiveTab('repair')}
                        >
                            Repair Orders (OOW)
                        </Button>
                    </Grid>
                </Grid>

                <Card>
                    <CardContent>
                        {/* Search input shared for both tabs */}
                        <Grid container spacing={2} alignItems="center">
                            {/* Warranty filters only visible on warranty tab */}
                            {activeTab === 'warranty' && (
                                <>
                                    <Grid item xs={12} sm={3} lg={3}>
                                        <Autocomplete
                                            size="small"
                                            fullWidth
                                            autoSelect
                                            disableClearable
                                            options={employeeList || []}
                                            getOptionLabel={option => (option ? option.Name : '')}
                                            value={selectedEmployee || null}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    label="Employee"
                                                    variant="outlined"
                                                />
                                            )}
                                            onChange={(event, value) => {
                                                dispatch(paymentActions.setSelectedEmployee(value));
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3} lg={3}>
                                        <Autocomplete
                                            size="small"
                                            fullWidth
                                            autoSelect
                                            disableClearable
                                            options={statusList || null}
                                            getOptionLabel={option => (option ? option.Name : '')}
                                            value={selectedStatus || null}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    label="Status"
                                                    variant="outlined"
                                                />
                                            )}
                                            onChange={(event, value) => {
                                                dispatch(paymentActions.setSelectedStatus(value));
                                                emptyTable(); // Statü değişince tabloyu temizle
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4} lg={4}>
                                        <ThemeProvider theme={materialTheme}>
                                            <MuiPickersUtilsProvider
                                                utils={DateFnsUtils}
                                                locale={localeMap[locale]}
                                            >
                                                <KeyboardDatePicker
                                                    color="primary"
                                                    inputVariant="outlined"
                                                    id="createDate"
                                                    size="small"
                                                    invalidDateMessage="Invalid date"
                                                    format="dd.MM.yyyy"
                                                    fullWidth
                                                    label="Create Date"
                                                    variant="outlined"
                                                    value={selectedCreateDate}
                                                    onChange={e => {
                                                        dispatch(paymentActions.setSelectedCreateDate(e));
                                                    }}
                                                    KeyboardButtonProps={{
                                                        'aria-label': 'change date'
                                                    }}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </ThemeProvider>
                                    </Grid>
                                    <Grid item xs={12} sm={2} lg={2}>
                                        <Button
                                            variant="contained"
                                            style={{
                                                backgroundColor: '#1E5128',
                                                color: 'white'
                                            }}
                                            fullWidth
                                            endIcon={<SearchIcon />}
                                            onClick={handleSearch}
                                        >
                                            Search
                                        </Button>
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={9}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    placeholder={activeTab === 'warranty' ? 'Search in all columns...' : 'Search product orders...'}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: <SearchIcon style={{ marginRight: 8, color: '#666' }} />
                                    }}
                                />
                            </Grid>
                            {activeTab === 'product' && (
                                <Grid item xs={3} sm={3} lg={3}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<CloudDownloadIcon />}
                                        onClick={exportProductOrders}
                                        style={{ textTransform: 'none', marginLeft: 12 }}
                                    >
                                        Export
                                    </Button>
                                </Grid>
                            )}
                            {activeTab === 'repair' && (
                                <Grid item xs={3} sm={3} lg={3}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<CloudDownloadIcon />}
                                        onClick={exportRepairOrders}
                                        style={{ textTransform: 'none', marginLeft: 12 }}
                                    >
                                        Export
                                    </Button>
                                </Grid>
                            )}
                        </Grid>

                        {/* Product Orders filters */}
                        {activeTab === 'product' && (
                            <Grid container spacing={2} alignItems="center" style={{ marginTop: 8 }}>
                                <Grid item xs={12} sm={3} lg={3}>
                                    <Autocomplete
                                        size="small"
                                        fullWidth
                                        clearable
                                        options={productOrderStatusList || []}
                                        getOptionLabel={option => (option ? option.Name : '')}
                                        value={productOrderStatusList && productSelectedStatusId ? productOrderStatusList.find(s => s.Id === productSelectedStatusId) || null : null}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                label="Order Status"
                                                variant="outlined"
                                            />
                                        )}
                                        onChange={(event, value) => {
                                            setProductSelectedStatusId(value ? value.Id : null);
                                            setProductPage(0);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3} lg={3}>
                                    <Autocomplete
                                        size="small"
                                        fullWidth
                                        clearable
                                        options={providerList || []}
                                        getOptionLabel={option => (option ? option.Name : '')}
                                        value={providerList && productSelectedProvider ? providerList.find(p => p.Id === productSelectedProvider) || null : null}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                label="Provider"
                                                variant="outlined"
                                            />
                                        )}
                                        onChange={(event, value) => {
                                            setProductSelectedProvider(value ? value.Id : null);
                                            setProductPage(0);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={2} lg={2}>
                                    <ThemeProvider theme={materialTheme}>
                                        <MuiPickersUtilsProvider
                                            utils={DateFnsUtils}
                                            locale={localeMap[locale]}
                                        >
                                            <KeyboardDatePicker
                                                color="primary"
                                                inputVariant="outlined"
                                                id="productStartDate"
                                                size="small"
                                                invalidDateMessage="Invalid date"
                                                format="dd.MM.yyyy"
                                                fullWidth
                                                label="Start Date"
                                                variant="outlined"
                                                value={productSelectedStartDate}
                                                onChange={e => {
                                                    setProductSelectedStartDate(e);
                                                    setProductPage(0);
                                                }}
                                                KeyboardButtonProps={{
                                                    'aria-label': 'change date'
                                                }}
                                            />
                                        </MuiPickersUtilsProvider>
                                    </ThemeProvider>
                                </Grid>
                                <Grid item xs={12} sm={2} lg={2}>
                                    <ThemeProvider theme={materialTheme}>
                                        <MuiPickersUtilsProvider
                                            utils={DateFnsUtils}
                                            locale={localeMap[locale]}
                                        >
                                            <KeyboardDatePicker
                                                color="primary"
                                                inputVariant="outlined"
                                                id="productEndDate"
                                                size="small"
                                                invalidDateMessage="Invalid date"
                                                format="dd.MM.yyyy"
                                                fullWidth
                                                label="End Date"
                                                variant="outlined"
                                                value={productSelectedEndDate}
                                                onChange={e => {
                                                    setProductSelectedEndDate(e);
                                                    setProductPage(0);
                                                }}
                                                KeyboardButtonProps={{
                                                    'aria-label': 'change date'
                                                }}
                                            />
                                        </MuiPickersUtilsProvider>
                                    </ThemeProvider>
                                </Grid>
                                <Grid item xs={12} sm={2} lg={2}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={() => {
                                            setProductSelectedStatusId(null);
                                            setProductSelectedProvider(null);
                                            setProductSelectedStartDate(null);
                                            setProductSelectedEndDate(null);
                                            setProductPage(0);
                                        }}
                                        style={{
                                            backgroundColor: '#d32f2f',
                                            color: 'white'
                                        }}
                                        size="small"
                                    >
                                        Clear
                                    </Button>
                                </Grid>
                            </Grid>
                        )}
                    </CardContent>

                    {/* Warranty table */}
                    {activeTab === 'warranty' && (
                        <TableContainer className={classes.container}>
                            <Table stickyHeader aria-label="sticky table" className={classes.table} size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left">Work Case</TableCell>
                                        <TableCell align="left">Work Order</TableCell>
                                        <TableCell align="left">Customer Name</TableCell>
                                        <TableCell align="left">Employee Name</TableCell>
                                        <TableCell align="left">Product</TableCell>
                                        <TableCell align="left">Price</TableCell>
                                        <TableCell align="left">Customer Phone</TableCell>
                                        <TableCell align="left">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredPaymentsData.map((row, index) => (
                                        <TableRow
                                            key={row.WorkCase}
                                            hover
                                            classes={{ hover: classes.hover }}
                                            className={classes.tableRow}
                                            onMouseEnter={() => handleMouseEnter(index)}
                                            onMouseLeave={handleMouseLeave}
                                            style={{ cursor: hoveredRowIndex === index ? 'pointer' : 'default' }}
                                        >
                                            <TableCell align="left">
                                                {row.WorkCaseId}
                                            </TableCell>
                                            <TableCell align="left">
                                                {row.WorkOrderId}
                                            </TableCell>
                                            <TableCell align="left">
                                                {row.CustomerName}
                                            </TableCell>
                                            <TableCell align="left">
                                                {row.EmployeeName}
                                            </TableCell>
                                            <TableCell align="left">
                                                {row.ProductName}
                                            </TableCell>
                                            <TableCell align="left">
                                                {row.Price}
                                            </TableCell>
                                            <TableCell align="left">
                                                {row.CustomerPhone}
                                            </TableCell>
                                            <TableCell align="left">
                                                {selectedStatus?.MobileStatusId === 70 && (
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        onClick={() => handleResend(row.WorkOrderId)}
                                                    >
                                                        Resend
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {/* Product orders table */}
                    {activeTab === 'product' && (
                        <TableContainer className={classes.container} style={{ overflowX: 'auto' }}>
                            <Table stickyHeader aria-label="product orders table" className={classes.table} size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left">Id</TableCell>
                                        <TableCell align="left">Order Status</TableCell>
                                        <TableCell align="left">Work Order Id</TableCell>
                                        <TableCell align="left">Create Date</TableCell>
                                        <TableCell align="left">Create Employee</TableCell>
                                        <TableCell align="left">Selected Employee</TableCell>
                                        <TableCell align="left">ERP Document Nr</TableCell>
                                        <TableCell align="left">Consumer Name</TableCell>
                                        <TableCell align="left">Consumer Phone</TableCell>
                                        <TableCell align="left">Consumer Address</TableCell>
                                        <TableCell align="left">Product SKU</TableCell>
                                        <TableCell align="left">Provider</TableCell>
                                        <TableCell align="left">Method</TableCell>
                                        <TableCell align="left">Total Paid Amount</TableCell>
                                        <TableCell align="left">{t('lblPaymentDetail')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {productPageItems.map((po, idx) => (
                                        <TableRow
                                            key={po.Id ?? (productPageStart + idx)}
                                            hover
                                            classes={{ hover: classes.hover }}
                                            className={classes.tableRow}
                                            onMouseEnter={() => handleMouseEnter(productPageStart + idx)}
                                            onMouseLeave={handleMouseLeave}
                                            style={{ cursor: hoveredRowIndex === (productPageStart + idx) ? 'pointer' : 'default' }}
                                            
                                        >
                                            <TableCell align="left">{po.Id}</TableCell>
                                            <TableCell align="left">{po.ProductOrderStatusName}</TableCell>
                                            <TableCell align="left">{po.WorkOrderId}</TableCell>
                                            <TableCell align="left">{po.CreateDate === undefined? '': Moment(po.CreateDate).format('DD/MM/YYYY-HH:mm:ss')}</TableCell>
                                            <TableCell align="left">{po.CreateEmployeeName}</TableCell>
                                            <TableCell align="left">{po.SelectedEmployeeName}</TableCell>
                                            <TableCell align="left">{po.ErpDocumentNr}</TableCell>
                                            <TableCell align="left">{po.ConsumerName}</TableCell>
                                            <TableCell align="left">{po.ConsumerPhone}</TableCell>
                                            <TableCell align="left">{po.ConsumerAddress}</TableCell>
                                            <TableCell align="left">{po.ProductSku}</TableCell>
                                            <TableCell align="left">{po.Provider}</TableCell>
                                            <TableCell align="left">{po.Method}</TableCell>
                                            <TableCell align="left">{SessionUser ? moneyFormat(po.TotalAmountPaid, SessionUser.TenantCode, SessionUser.CurrencyUnit) + ' ' + SessionUser.CurrencyUnit : 'N/A'}</TableCell>
                                            <TableCell align="left">
                                                <div style={{ display: 'flex', gap: 4 }}>
                                                {po.Provider === 'BankTransfer' && po.ProductOrderStatusId == 1 ? (
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        onClick={() => handleApproveDialogClick(po.Id, po.Provider, po.ProductOrderStatusId)}
                                                    >
                                                        <Edit/>
                                                    </Button>
                                                ) 
                                                : (
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        size="small"
                                                        onClick={() => handleDetailDialogOpen(po.Id, po.ConsumerId)}
                                                    >
                                                    <Edit/>

                                                    </Button>
                                                    //<Typography variant="body2">-</Typography>
                                                )}
                                                {po.ProductOrderStatusId === 3 && !po.ErpDocumentNr && (
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        size="small"
                                                        startIcon={<SendIcon />}
                                                        onClick={() => handleSendToSap(po.Id)}
                                                        style={{ textTransform: 'none' }}
                                                    >
                                                        Send to SAP
                                                    </Button>
                                                )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {/* Pagination controls */}
                            <Grid container justifyContent="center" alignItems="center" style={{ padding: 12 }}>
                                <Button
                                    size="small"
                                    onClick={() => setProductPage(p => Math.max(0, p - 1))}
                                    disabled={productPage === 0}
                                    style={{ textTransform: 'none', marginRight: 8 }}
                                >
                                    Prev
                                </Button>
                                <Typography variant="caption" style={{ fontWeight: 600 }}>
                                    Page {productPage + 1} / {productTotalPages}
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={() => setProductPage(p => Math.min(productTotalPages - 1, p + 1))}
                                    disabled={productPage >= productTotalPages - 1}
                                    style={{ textTransform: 'none', marginLeft: 8 }}
                                >
                                    Next
                                </Button>

                            </Grid>
                        </TableContainer>
                    )}

                    {/* Repair Orders (OOW) table */}
                    {activeTab === 'repair' && (
                        <TableContainer className={classes.container} style={{ overflowX: 'auto' }}>
                            <Table stickyHeader aria-label="repair orders table" className={classes.table} size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left">Id</TableCell>
                                        <TableCell align="left">Work Order Id</TableCell>
                                        <TableCell align="left">Create Date</TableCell>
                                        <TableCell align="left">Create Employee</TableCell>
                                        <TableCell align="left">Consumer Name</TableCell>
                                        <TableCell align="left">Provider</TableCell>
                                        <TableCell align="left">Method</TableCell>
                                        <TableCell align="left">Payment Reference</TableCell>
                                        <TableCell align="left">Total Amount with VAT</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {repairPageItems.map((ro, idx) => (
                                        <TableRow
                                            key={ro.Id ?? (repairPageStart + idx)}
                                            hover
                                            classes={{ hover: classes.hover }}
                                            className={classes.tableRow}
                                            onMouseEnter={() => handleMouseEnter(repairPageStart + idx)}
                                            onMouseLeave={handleMouseLeave}
                                            style={{ cursor: 'default' }}
                                        >
                                            <TableCell align="left">{ro.Id}</TableCell>
                                            <TableCell align="left">{ro.WorkOrderId}</TableCell>
                                            <TableCell align="left">{ro.CreateDate ? Moment(ro.CreateDate).format('DD/MM/YYYY HH:mm:ss') : ''}</TableCell>
                                            <TableCell align="left">{ro.CreateEmployeeName}</TableCell>
                                            <TableCell align="left">{ro.ConsumerName}</TableCell>
                                            <TableCell align="left">{ro.Provider}</TableCell>
                                            <TableCell align="left">{ro.Method}</TableCell>
                                            <TableCell align="left">{ro.PaymentReference}</TableCell>
                                            <TableCell align="left">
                                                {ro.TotalAmount != null
                                                    ? moneyFormat(ro.TotalAmount * 1.21, SessionUser?.TenantCode, SessionUser?.CurrencyUnit) + ' ' + (SessionUser?.CurrencyUnit || '')
                                                    : ''}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {/* Pagination controls */}
                            <Grid container justifyContent="center" alignItems="center" style={{ padding: 12 }}>
                                <Button
                                    size="small"
                                    onClick={() => setRepairPage(p => Math.max(0, p - 1))}
                                    disabled={repairPage === 0}
                                    style={{ textTransform: 'none', marginRight: 8 }}
                                >
                                    Prev
                                </Button>
                                <Typography variant="caption" style={{ fontWeight: 600 }}>
                                    Page {repairPage + 1} / {repairTotalPages}
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={() => setRepairPage(p => Math.min(repairTotalPages - 1, p + 1))}
                                    disabled={repairPage >= repairTotalPages - 1}
                                    style={{ textTransform: 'none', marginLeft: 8 }}
                                >
                                    Next
                                </Button>
                            </Grid>
                        </TableContainer>
                    )}
                </Card>

                {/* Payment Dialog */}
                <Dialog
                    open={approveDialogOpen}
                    onClose={handleApproveCancel}
                    aria-labelledby="approve-dialog-title"
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle id="approve-dialog-title" style={{ textAlign: 'center' }}>
                        {' '}
                        {t('lblPaymentDetails')}
                    </DialogTitle>
                    <Container maxWidth="lg" spacing={1}>
                        <Divider />
                        <Card>
                            <CardHeader title={`${t('lblOrderId')}: ${selectedOrderId}`} />
                            <Divider />
                            <CardContent className={classes.cardscontent}>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} sm={12} lg={12}>
                                    <Autocomplete
                                        size="small"
                                        fullWidth
                                        autoSelect
                                        disableClearable={true}
                                        value={approvalType ? { id: approvalType, label: approvalType === 'approved' ? t('lblpaymentApproved') : t('lblpaymentRejected') } : null}
                                        options={[
                                            { id: 'approved', label: t('lblpaymentApproved') },
                                            { id: 'rejected', label: t('lblpaymentRejected') }
                                        ]}
                                        getOptionLabel={(option) => (option ? option.label : '')}
                                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={t('lblapprovalType')}
                                                variant="outlined"
                                                error={!approvalType}
                                            />
                                        )}
                                        onChange={(event, value) => {
                                            setApprovalType(value ? value.id : null);
                                        }}
                                    />
                                    </Grid>
                                    <Grid item xs={12} sm={12} lg={12}>
                                        <TextField
                                            size="small"
                                            id="description"
                                            multiline
                                            minRows={8}
                                            autoComplete="no"
                                            fullWidth
                                            label={t('lbldescription')}
                                            variant="outlined"
                                            value={description}
                                            onChange={(event) => setDescription(
                                                event.target.value
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                        <Divider />
                        <DialogActions>
                            <Button onClick={handleApproveCancel} color="primary" variant="contained" endIcon={<Close />}>
                                {t('lblCancel')}
                            </Button>
                            <Button onClick={handleApproveConfirm} color="primary" variant="contained" disabled={!approvalType} endIcon={<Save />}>
                                {t('lblSave')}
                            </Button>
                        </DialogActions>

                    </Container>
                </Dialog>
                {/* Product Order Detail Dialog */}
                <Dialog
                    open={detailDialogOpen}
                    onClose={handleDetailDialogClose}
                    aria-labelledby="detail-dialog-title"
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle id="detail-dialog-title">
                        Product Order Details
                    </DialogTitle>
                    <DialogContent>
                        {selectedDetailOrderId && (
                            <ProductOrderDetail
                                OrderId={selectedDetailOrderId}
                                consumerId={selectedDetailConsumerId}
                            />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={handleDetailDialogClose}
                            color="primary"
                            variant="contained"
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Page>
    );
};

export default SironexPayments;